import React, { useEffect, useMemo, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

// store
import { storeSet, useStoreValue } from '../../../../store';

// components
import StatusIconComponent from '../../../../components/StatusIconComponent';
import ButtonIconSimple from '../../../../components/ButtonWrapper/components/ButtonIconSimple';
import ButtonIconSimpleDoubleConfirmation from '../../../../components/ButtonWrapper/components/ButtonIconSimpleDoubleConfirmation';
import DeleteIcon from '../../../../icons/DeleteIcon';
import DownloadIcon from '../../../../icons/DownloadIcon';
import { getCueSheetStatusLabel } from '../../../../helpers/cueSheetStatuses';
import BadgeTrack from '../../../../components/BadgeTrack';
import { cueSheetTypes } from '../../../../configData';
import SfxIcon from '../../../../icons/SfxIcon';
import MixIcon from '../../../../icons/MixIcon';
import ModalUploadCueSheetClip from '../../../../components/ModalUploadCueSheetClip';
import ClipService from '../../../../service/clip-service';
import displayNetworkError from '../../../../helpers/displayNetworkError';
import ModalAssignMemberToCuesheetItem from '../../../../components/ModalAssignMemberToCuesheetItem';

// hooks
import useSelection from '../../../../hooks/useSelection';
import { SELECTION_KINDS } from '../../../../helpers/selection';

/**
 * Rows are multi-selectable: a plain click selects one, ctrl / cmd adds or
 * removes one, shift takes the range from the last click. The selection lives
 * in the store, so the toolbar above (Modifica, Elimina) and the Comm Center
 * beside it both read the same thing.
 */
const ProjectCueSheet = ({ projectId, type }) => {
    const cueSheet = useStoreValue('cueSheet');
    const cueType = cueSheetTypes[type];
    const finalCuseSheetItems = cueSheet.filter(o => o.cueType === cueType);
    const lastProjectId = useRef(null);

    const orderedIds = useMemo(
        () => finalCuseSheetItems.map(item => item.id),
        [finalCuseSheetItems]
    );
    const { isSelected, select } = useSelection(SELECTION_KINDS.CUE, orderedIds);

    const handleDeleteCueSheetItem = async (id) => {
        try {
            await storeSet('deleteCueSheetItem', projectId, id);
        } catch (error) {
            // Error already displayed by store action
        }
    }

    const handleDownloadCueSheetItem = async (clip) => {
        try {
            const data = await ClipService.downloadClipAsync(clip.id);
            const file = new Blob([data], { type: clip.mimeType });
            const url = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', clip.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            displayNetworkError(error);
            throw error;
        }
    }

    useEffect(() => {
        storeSet('fetchCueSheet', projectId);
        if (lastProjectId.current !== projectId) {
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    return(
        <div className="projectCueSheet" role="table">
            <div className="projectCueSheet__header" role="rowgroup">
                <div className="projectCueSheet__row projectCueSheet__row--header" role="row">
                    <div className="projectCueSheet__cell projectCueSheet__cell--track" role="columnheader">{__('Track', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--titolo" role="columnheader">{__('Titolo', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--version" role="columnheader">{__('v', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--timecode" role="columnheader">{__('Timecode', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--assigned" role="columnheader">{__('Assegnato a', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--status" role="columnheader">{__('Status', 'soundbetween')}</div>
                    <div className="projectCueSheet__cell projectCueSheet__cell--actions" role="columnheader"></div>
                </div>
            </div>
            <OverlayScrollbarsComponent element="div" className="projectCueSheet__body" options={{ scrollbars: { autoHide: 'never' } }} defer role="rowgroup">
            <div className="projectCueSheet__list">
            {finalCuseSheetItems.map(o => {
                const rowSelected = isSelected(o.id);
                const handleRowKeyDown = (e) => {
                    // Keys typed in the modals rendered inside the row bubble up here
                    // through the portal; only the focused row itself toggles.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        select(o.id, e);
                    }
                };
                return <div
                    className={`projectCueSheet__row projectCueSheet__row--body${rowSelected ? ' projectCueSheet__row--selected' : ''}`}
                    role="row"
                    key={o.id}
                    onClick={(e) => {
                        // Buttons and modals inside the row must not toggle the
                        // selection. `dialog` is listed explicitly: the modals are
                        // <dialog> elements rendered inside the row, and their
                        // implicit ARIA role is not matched by an attribute
                        // selector, so every click inside one reached the row.
                        if (e.target.closest('button, a, input, textarea, dialog, [role="dialog"]')
                            || !e.currentTarget.contains(e.target)) return;
                        select(o.id, e);
                    }}
                    onKeyDown={handleRowKeyDown}
                    tabIndex={0}
                    aria-selected={rowSelected}
                    style={{ cursor: 'pointer' }}>

                <div className="projectCueSheet__cell projectCueSheet__cell--track" role="cell">
                    {type === 'music' ? <BadgeTrack>{o.label}</BadgeTrack> : null}
                    {type === 'sfx' ? <SfxIcon/> : null}
                    {type === 'mix' ? <MixIcon/> : null}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--titolo" role="cell">
                    {o.title}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--version" role="cell">
                    {o.version}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--timecode" role="cell">
                    {o.timecode ?? '-:-'}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--assigned" role="cell">
                    <ModalAssignMemberToCuesheetItem
                        projectId={projectId}
                        cueSheetId={o.id}
                        assignees={o.assignees ?? (o.assignedUser ? [o.assignedUser] : [])}
                        assigneeUserIds={o.assigneeUserIds ?? (o.assignedUserId ? [o.assignedUserId] : [])}/>
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--status" role="cell">
                    <StatusIconComponent status={o.status}></StatusIconComponent> {getCueSheetStatusLabel(o.status)}
                </div>
                <div className="projectCueSheet__cell projectCueSheet__cell--actions" role="cell">
                    <div className="projectCueSheet__actions">
                        <ButtonIconSimpleDoubleConfirmation
                            onConfirm={() => handleDeleteCueSheetItem(o.id)}
                            confirmTimeout={4000}
                            ariaLabel={__('Elimina', 'soundbetween')}
                            ariaLabelWarning={__('Clicca di nuovo per confermare l’eliminazione', 'soundbetween')}>
                            <DeleteIcon/>
                        </ButtonIconSimpleDoubleConfirmation>
                        {o.clip ? <ButtonIconSimple
                            onClick={() => handleDownloadCueSheetItem(o.clip)}>
                            <DownloadIcon/>
                        </ButtonIconSimple> : null}
                        {!o.clip
                            ? <ModalUploadCueSheetClip projectId={projectId} cueSheetItem={o}/>
                            : null}
                    </div>
                </div>
            </div>;
            })}
            </div>
            </OverlayScrollbarsComponent>
        </div>
    )
}

export default ProjectCueSheet;